/**
 * Timeout constants — derived from shared/timeouts.json (single source of truth).
 *
 * Mobile timeouts are intentionally larger than backend timeouts to avoid
 * false client-side timeouts. The buffer accounts for network latency and
 * ensures the server has time to respond with a proper error before the
 * client gives up.
 */

import sharedTimeouts from '../generated/shared/timeouts.json';

// ── Backend timeout values (for reference / derivation) ──────────────

/** Backend analysis timeout (AI provider HTTP request) */
export const SERVER_ANALYSIS_TIMEOUT_MS = sharedTimeouts.analysisTimeoutMs;

/** Backend conversation stream timeout */
export const SERVER_CONVERSATION_TIMEOUT_MS = sharedTimeouts.conversationTimeoutMs;

/** Backend DB operation timeout */
export const SERVER_DB_TIMEOUT_MS = sharedTimeouts.dbTimeoutMs;

// ── Server-TTS auto-play stall guards ────────────────────────────────

/**
 * Per-segment server-TTS fetch timeout (`POST /api/speech/tts`).
 *
 * The shared axios client's `HTTP_TIMEOUT_MS` (~85s) is sized for the slow
 * analysis-retry endpoint — far too long for a single TTS segment, whose
 * legit round-trip tops out around ~1.5s on staging. Without a tight bound
 * a hung POST leaves the auto-play pump's prefetch slot occupied forever and
 * silently freezes mid-reply. This caps each fetch so it aborts and rejects
 * (routing to the session's device-TTS fallback) instead of hanging.
 */
export const TTS_FETCH_TIMEOUT_MS = sharedTimeouts.ttsFetchTimeoutMs;

/**
 * Server-TTS auto-play session watchdog window.
 *
 * A "no forward progress" backstop for the streaming session: it resets on
 * every real progress event (a fetch resolves, a segment starts playing, a
 * playback completes) and only fires when the window elapses with work still
 * pending. Catches freezes the per-segment fetch timeout can't — e.g. a
 * playback `done` callback that never settles. Sized a little above
 * `TTS_FETCH_TIMEOUT_MS` so a legitimately slow-but-progressing fetch never
 * trips it.
 */
export const TTS_SESSION_WATCHDOG_MS = sharedTimeouts.ttsSessionWatchdogMs;

/**
 * Upper bound on ONE replay chunk's `playback.done` wait (ttsStore
 * speakWithServer). expo-audio's finish event can silently never fire (the
 * same failure class the auto-play watchdog recovers from), and the replay
 * path otherwise awaits it forever — a stuck speaker button with no error and
 * no fallback. Generous: must exceed the audio duration of the longest chunk
 * (the request cap is 800 chars ≈ ~60s spoken), because hitting the guard
 * aborts server playback and reroutes the REMAINDER to device TTS.
 */
export const TTS_REPLAY_PLAYBACK_MAX_MS = sharedTimeouts.ttsReplayPlaybackMaxMs;

// ── Mobile-derived timeouts ──────────────────────────────────────────

/** Buffer added to server timeouts for network latency */
const NETWORK_BUFFER_MS = 15_000;

/**
 * Long-op HTTP budget — the analyze endpoint runs a 60s pronunciation model
 * server-side, so its round-trip legitimately needs ~85s.
 *
 * This is NOT the Axios instance default (see `HTTP_DEFAULT_TIMEOUT_MS`): only
 * the handful of genuinely-slow calls opt into it per-request via
 * `config.timeout`. Sizing every request for the slowest one is what turned an
 * abrupt network transition into a multi-minute dead zone — a request stuck on
 * a stale pooled TCP socket hung the full 85s before aborting. See
 * `docs/specs/mobile/network-resilience.mobile.md`.
 */
export const HTTP_TIMEOUT_MS = SERVER_ANALYSIS_TIMEOUT_MS + NETWORK_BUFFER_MS + SERVER_DB_TIMEOUT_MS;

/**
 * Per-request timeout override for slow, LLM-backed HTTP calls (pronunciation
 * analysis, lesson/exercise/quick-practice generation, AI search). These
 * invoke an AI provider server-side and can legitimately run far past the
 * fail-fast default, so each such call site passes `{ timeout: HTTP_AI_TIMEOUT_MS }`.
 *
 * NOTE for future call sites: any new `api.*` call that hits an AI-generation
 * endpoint MUST use this override, or it will spuriously time out at 20s.
 */
export const HTTP_AI_TIMEOUT_MS = HTTP_TIMEOUT_MS;

/**
 * Fail-fast default timeout for ordinary HTTP requests.
 *
 * Deliberately short: after a network transition (Wi-Fi drop/restore,
 * Wi-Fi↔cellular handoff) the OS holds stale, half-open TCP connections in its
 * pool. A request dispatched onto a dead connection hangs until it aborts. A
 * tight default means it aborts in ~20s instead of ~85s, so the retry in
 * `apiClient.ts` establishes a *fresh* connection promptly. Ordinary reads
 * complete in well under this; slow-but-legit calls use a per-request override.
 */
export const HTTP_DEFAULT_TIMEOUT_MS = 20_000;

// ── HTTP transport-retry tuning (apiClient response interceptor) ─────

/** Max automatic retries for idempotent (GET/HEAD) transport failures. */
export const HTTP_MAX_RETRIES = 2;

/** Base backoff before the first retry; grows by `HTTP_RETRY_FACTOR` per attempt. */
export const HTTP_RETRY_BASE_MS = 400;

/** Backoff growth factor between retry attempts. */
export const HTTP_RETRY_FACTOR = 2.5;

/** ±fraction of jitter applied to each backoff delay (matches SyncQueue's shape). */
export const HTTP_RETRY_JITTER = 0.3;

/**
 * Socket listener timeout for analysis events.
 * Must be > SERVER_ANALYSIS_TIMEOUT_MS so the server can timeout and
 * send analysis_failed before the client gives up.
 */
export const ANALYSIS_LISTENER_TIMEOUT_MS = SERVER_ANALYSIS_TIMEOUT_MS + NETWORK_BUFFER_MS;

/**
 * Socket listener timeout for conversation completion (user-message path).
 *
 * Client-side safety net for when the server dies mid-stream and neither
 * `done` nor an `{op}_error` event arrives. Sized to outlive the worst-case
 * server pipeline (analysis + conversation + DB persist) + network buffer.
 *
 * Note: analysis and conversation actually run in parallel on the
 * backend, so the *real* worst-case is closer to `max(analysis,
 * conversation) + DB + buffer = 85s`. The current sum keeps a defensive
 * safety margin in case the backend ever serializes them or the
 * provider blocks analysis on conversation completion.
 *
 * With the 30s `STREAM_STALL_TIMEOUT_MS` heartbeat below, this upper
 * bound only matters for "tokens dribble for >100s without ever
 * completing" — exceedingly rare in practice.
 */
export const CONVERSATION_TIMEOUT_MS =
  SERVER_ANALYSIS_TIMEOUT_MS + SERVER_CONVERSATION_TIMEOUT_MS + SERVER_DB_TIMEOUT_MS + NETWORK_BUFFER_MS;

/**
 * Socket listener timeout for AI-initiated session openings, i.e. the
 * AI's first turn that fires automatically after `start_session` when
 * the flow declares `hasOpening: true`.
 *
 * This is **not** the budget for a user-typed first message — when the
 * flow has no AI opening, the user's first turn is just a regular
 * `send_message` and uses `CONVERSATION_TIMEOUT_MS` like any other
 * user message. The two paths are:
 *
 *   - AI-initiated opening   → `start_session`  → AI_OPENING_TIMEOUT_MS
 *   - User-initiated opening → `send_message`   → CONVERSATION_TIMEOUT_MS
 *
 * The AI-initiated path carries no audio (no user speech to analyze),
 * so the backend's 60s pronunciation-analysis budget is dead weight.
 * Budget = conversation stream + DB persist + network buffer = ~85s.
 *
 * Like `CONVERSATION_TIMEOUT_MS`, this works alongside the 30s
 * `STREAM_STALL_TIMEOUT_MS` heartbeat — stall catches "no proof of
 * life", this catches "tokens dribble forever without `done`".
 */
export const AI_OPENING_TIMEOUT_MS =
  SERVER_CONVERSATION_TIMEOUT_MS + SERVER_DB_TIMEOUT_MS + NETWORK_BUFFER_MS;

/**
 * Cooldown between auth email sends (AUTH-012 password-reset resend,
 * AUTH-013 sign-up confirmation resend).
 *
 * Mirrors Supabase `max_frequency = "60s"` under `[auth.email]` in
 * supabase/config.toml — the window is global to all auth emails, not
 * recovery-specific. The server rejects a re-send inside it
 * (`over_email_send_rate_limit`), so the UI disables the resend affordance
 * with a countdown instead of surfacing the error after the fact.
 */
export const AUTH_RESEND_COOLDOWN_MS = 60_000;

/**
 * "No proof of life" guard for in-flight streams. Default 30s.
 *
 * `CONVERSATION_TIMEOUT_MS` is the worst-case upper bound for a legitimate
 * but slow request — 145s of staring at a spinner if the backend stops
 * responding entirely. At 145s most users will think the app is broken.
 *
 * This shorter timer resets on every inbound stream event (token,
 * stream_complete, {op}_error, done, session_transition, message_saved).
 * If the window elapses with *no* event at all, the stream is treated
 * as stalled and the spinner is replaced with a retryable error bubble.
 *
 * Tuned to be longer than the slowest realistic time-to-first-event
 * (cold-cache LLM cluster + DB write to record `message_saved`) but
 * short enough to fail visibly fast when nothing is actually coming.
 *
 * 30s = first inbound event ought to arrive within ~3s on a healthy
 * backend; 10× that gives plenty of headroom for legitimate slow paths
 * (cold cluster, slow upstream provider) while still bounding the
 * silent-failure UX.
 */
export const STREAM_STALL_TIMEOUT_MS = 30_000;

/**
 * Analysis "no proof of life" stall guard. Default 20s.
 *
 * Mirrors `STREAM_STALL_TIMEOUT_MS` but for the pronunciation-analysis
 * event family (`analyzing` / `analysis_complete` / `analysis_failed`).
 * The existing `ANALYSIS_LISTENER_TIMEOUT_MS` (75s) is the upper bound
 * for the case where `analyzing` fires (analysis is in progress) but
 * `analysis_complete` never lands — sized to the worst-case Azure
 * pronunciation-analysis budget + a buffer.
 *
 * This shorter guard fires when NOTHING arrives — meaning the
 * backend never even started analysis. Cuts the "user message has a
 * spinning analysisPending badge for over a minute" UX down to ~20s.
 *
 * 20s = first `analyzing` event ought to arrive within ~2-5s on a
 * healthy backend; the 4-10× buffer covers cold-start / slow paths.
 */
export const ANALYSIS_STALL_TIMEOUT_MS = 20_000;

/**
 * Live (override-able) stall timeout. Production reads the constant
 * above; the `dev.override-stall-timeout` dev intent can mutate this at
 * runtime so E2E stories can knock the wait down to ~3s without
 * waiting 30s × N branches per CI run.
 *
 * Always exposed through `getStreamStallTimeoutMs()` so consumers pick
 * up overrides without re-importing — the constant itself stays
 * immutable so production code can still reason about it statically.
 */
let _currentStreamStallTimeoutMs = STREAM_STALL_TIMEOUT_MS;
export const getStreamStallTimeoutMs = (): number => _currentStreamStallTimeoutMs;
export const overrideStreamStallTimeoutMs = (ms: number | null): void => {
  _currentStreamStallTimeoutMs = ms === null ? STREAM_STALL_TIMEOUT_MS : ms;
};
